import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip, { chipClasses } from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

function getStatusColorByTone(theme) {
  return {
    waiting: theme.palette.mode === 'light' ? 'hsl(45, 90%, 40%)' : 'hsl(45, 90%, 45%)',
    analysis: theme.palette.mode === 'light' ? 'hsl(32, 90%, 45%)' : 'hsl(32, 90%, 55%)',
    report: (theme.vars || theme).palette.error.main,
    finalized: (theme.vars || theme).palette.success.main,
    total: theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.grey[400],
  };
}

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

function getValueFontSize(value, valueSize) {
  const text = String(value ?? '');
  const length = text.length;

  if (valueSize === 'compact') {
    if (length >= 14)
      return 'clamp(0.95rem, 1.85vw, 1.2rem)';
    return 'clamp(1.05rem, 2.1vw, 1.45rem)';
  }

  if (valueSize === 'financial') {
    if (length >= 16)
      return 'clamp(0.95rem, 2vw, 1.35rem)';
    if (length >= 12)
      return 'clamp(1.02rem, 2.15vw, 1.5rem)';
    return 'clamp(1.12rem, 2.4vw, 1.75rem)';
  }

  if (length >= 14)
    return 'clamp(1rem, 2.2vw, 1.55rem)';

  return 'clamp(1.2rem, 2.8vw, 1.95rem)';
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function StatCard({ title, value, interval, trend, variation, data, labels, valueFormatter, statusTone, valueSize }) {
  const theme = useTheme();
  const statusColorByTone = getStatusColorByTone(theme);

  const trendColors = {
    up:
      theme.palette.mode === 'light'
        ? theme.palette.success.main
        : theme.palette.success.dark,
    warning:
      theme.palette.mode === 'light'
        ? theme.palette.warning.main
        : theme.palette.warning.dark,
    down:
      theme.palette.mode === 'light'
        ? theme.palette.error.main
        : theme.palette.error.dark,
    neutral:
      theme.palette.mode === 'light'
        ? theme.palette.grey[400]
        : theme.palette.grey[700],
  };

  const toneStyles = {
    total: {
      chipColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
    },
    waiting: {
      chipColor: theme.palette.common.white,
    },
    analysis: {
      chipColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
    },
    report: {
      chipColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
    },
    finalized: {
      chipColor: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.grey[900],
    },
  };

  const statusStyle = statusTone ? toneStyles[statusTone] : null;
  const chartColor = (statusTone && statusColorByTone[statusTone]) || trendColors[trend];
  const trendValues = { up: '+25%', down: '-25%', neutral: '+5%' };
  const chipLabel = variation || trendValues[trend];

  return (
    <Card variant="outlined" sx={{ height: '100%', flexGrow: 1 }}>
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%' }}>
        <Typography component="h2" variant="subtitle2" gutterBottom sx={{ minHeight: 20 }}>
          {title}
        </Typography>
        <Stack
          direction="column"
          sx={{ justifyContent: 'space-between', flexGrow: 1, gap: 1 }}
        >
          <Stack sx={{ justifyContent: 'space-between', gap: 0.75 }}>
            <Stack
              direction="column"
              sx={{ gap: 0.5 }}
            >
              <Stack
                direction="row"
                sx={{ width: '100%', justifyContent: 'flex-end' }}
              >
                <Chip
                  size="small"
                  label={chipLabel}
                  sx={statusStyle ? {
                    backgroundColor: chartColor,
                    borderColor: chartColor,
                    [`& .${chipClasses.label}`]: {
                      color: statusStyle.chipColor,
                      whiteSpace: 'nowrap',
                    },
                  } : undefined}
                />
              </Stack>
              <Typography
                variant="h4"
                component="p"
                sx={{
                  fontSize: getValueFontSize(value, valueSize),
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                {value}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
              {interval}
            </Typography>
          </Stack>
          <Box sx={{ width: '100%', height: 50 }}>
            <SparkLineChart
              color={chartColor}
              data={data}
              valueFormatter={valueFormatter}
              area
              showHighlight
              showTooltip
              xAxis={{
                scaleType: 'band',
                data: labels, // Use the correct property 'data' for xAxis
              }}
              sx={{
                [`& .${areaElementClasses.root}`]: {
                  fill: `url(#area-gradient-${value})`,
                },
              }}
            >
              <AreaGradient color={chartColor} id={`area-gradient-${value}`} />
            </SparkLineChart>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

StatCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  interval: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  trend: PropTypes.oneOf(['down', 'neutral', 'up', 'warning']).isRequired,
  value: PropTypes.string.isRequired,
  variation: PropTypes.string,
  valueFormatter: PropTypes.func,
  statusTone: PropTypes.oneOf(['total', 'waiting', 'analysis', 'report', 'finalized']),
  valueSize: PropTypes.oneOf(['default', 'financial', 'compact']),
};

export default StatCard;
